import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { alertText, markAlertsSeen, openBookPath, startAlertPolling, useReadyAlerts, type ReadyAlert } from '@/lib/projects-api';
import { cn } from '@/lib/utils';

// THE READY NOTICE (Studio Blueprint 04, owner 2026-10-16): when a render
// finishes while the buyer is anywhere on the site, a card says so with an
// Open button. It floats above the page (fixed, so nothing below it moves),
// over the phone tab bar's top edge and in the desktop's bottom-right corner.
// Opening it or pressing Later marks it seen; the Projects page lists every
// one of them anyway, so the notice stays out of the way there.

export function openTarget(open: ReadyAlert['open']): string | null {
  return typeof open === 'string' ? open : null;
}

export function ReadyNotice() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const alerts = useReadyAlerts();
  const [busy, setBusy] = useState(false);

  useEffect(() => (isAuthenticated ? startAlertPolling() : undefined), [isAuthenticated]);

  const alert = alerts[0];
  if (!isAuthenticated || !alert || pathname.startsWith('/projects')) return null;

  const failed = alert.state === 'failed';
  const more = alerts.length - 1;

  const open = async () => {
    setBusy(true);
    try {
      let to = openTarget(alert.open);
      if (!to && typeof alert.open === 'object' && alert.open && 'book' in alert.open) to = await openBookPath(alert.open.book);
      await markAlertsSeen([alert.id]);
      navigate(to || '/projects');
    } catch {
      navigate('/projects');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="ready-notice"
      className="fixed inset-x-3 bottom-[calc(var(--tabbar-h)+env(safe-area-inset-bottom)+0.75rem)] z-40 mx-auto max-w-md md:inset-x-auto md:bottom-6 md:right-6 md:mx-0 md:w-[380px]"
    >
      <div className="hero-hairline relative overflow-hidden rounded-[1.35rem] border border-white/10 bg-[#11151d]/95 p-4 shadow-[0_24px_48px_-20px_rgba(0,0,0,0.9)] backdrop-blur-xl">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset',
              failed ? 'bg-destructive/10 text-destructive ring-destructive/30' : 'bg-accent/[0.12] text-accent ring-accent/30',
            )}
          >
            {failed ? <AlertTriangle aria-hidden className="h-5 w-5" /> : <CheckCircle2 aria-hidden className="h-5 w-5" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-[15px] font-bold leading-snug text-foreground">{alertText(alert, i18n.language)}</p>
            <p className="mt-0.5 text-[13px] text-muted-foreground">
              {failed ? t('projects.notice.failedLead') : t('projects.notice.readyLead')}
              {more > 0 && <> · {t('projects.notice.more', { count: more })}</>}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void markAlertsSeen([alert.id])}
            className="-mr-1 -mt-1 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-white/10 hover:text-foreground"
            aria-label={t('projects.notice.later')}
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void open()}
            data-testid="ready-notice-open"
            className="min-h-[44px] flex-1 rounded-full border border-accent/40 bg-accent/15 px-5 font-semibold text-accent hover:bg-accent/25 disabled:opacity-60"
          >
            {failed ? t('projects.notice.see') : t('projects.notice.open')}
          </button>
          <button
            type="button"
            onClick={() => void markAlertsSeen([alert.id])}
            className="min-h-[44px] rounded-full border border-white/15 bg-white/[0.06] px-5 font-semibold hover:border-primary/50 hover:bg-primary/10"
          >
            {t('projects.notice.later')}
          </button>
        </div>
      </div>
    </div>
  );
}
